import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import PageTransition from '../components/ui/PageTransition';
import HeroSection from '../components/sections/HeroSection';

const QuotePage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Contact Us | Consiliuma Nottingham</title>
        <meta name="description" content="We operate with a no spam, no data collection policy. Contact Consiliuma via Telegram for a fast, private response." />
        <meta name="keywords" content="Web Design Contact, No Spam, No Data Collection, Telegram, Consiliuma" />
        <link rel="canonical" href="/quote" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Contact Us | Consiliuma Nottingham" />
        <meta property="og:description" content="We operate with a no spam, no data collection policy. Contact Consiliuma via Telegram for a fast, private response." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://consiliuma.co.uk/quote" />
        <meta property="og:image" content="https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg" />
        <meta property="og:site_name" content="Consiliuma" />
        <meta property="og:locale" content="en_GB" />

        {/* Schema.org data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Contact Us | Consiliuma Nottingham",
            "description": "We operate with a no spam, no data collection policy. Contact Consiliuma via Telegram for a fast, private response.",
            "url": "https://consiliuma.co.uk/quote",
            "mainEntity": {
              "@type": "Service",
              "name": "Web Design and Development Services",
              "provider": {
                "@type": "Organization",
                "name": "Consiliuma",
                "url": "https://consiliuma.co.uk"
              },
              "serviceType": ["Web Design", "Web Development", "E-commerce Development", "Web Application Development"]
            }
          })}
        </script>
      </Helmet>

      <main>
        <h1 className="sr-only">Contact Consiliuma</h1>

        <HeroSection
          title="Contact Us"
          subtitle="No spam. No data collection. Just direct, private communication."
          ctaText="Message on Telegram"
          ctaLink="https://t.me/ConsiliumaDesign"
          backgroundImage="https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg"
        />

        {/* Contact Policy Section */}
        <section className="py-16 md:py-24" aria-labelledby="contact-policy-heading">
          <div className="container-custom max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-md border-l-4 border-primary"
            >
              <h2 id="contact-policy-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Our No Spam, No Data Collection Policy
              </h2>
              <p className="text-xl text-secondary mb-6">
                We believe your privacy is paramount. That’s why we don’t use contact forms, don’t collect your data, and never send spam.
              </p>
              <p className="text-secondary mb-6">
                To get in touch, simply message us directly on Telegram. You’ll speak to a real person—no bots, no mailing lists, no unwanted follow-ups.
              </p>
              <div className="flex items-center gap-4 mt-8">
                <a
                  href="https://t.me/ConsiliumaDesign"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg shadow hover:bg-primary-dark transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9.036 15.956l-.396 4.053c.567 0 .813-.244 1.112-.537l2.664-2.522 5.522 4.032c1.012.557 1.726.264 1.98-.937l3.592-16.84c.327-1.527-.553-2.127-1.54-1.76L2.29 9.47c-1.49.58-1.472 1.41-.254 1.786l4.37 1.366 10.164-6.41c.478-.309.914-.138.555.171"/>
                  </svg>
                  Message @ConsiliumaDesign
                </a>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                Prefer email? Reach us at <a href="mailto:hello@consiliuma.co.uk" className="underline text-primary">hello@consiliuma.co.uk</a>
              </p>
            </motion.div>
          </div>
        </section>

        {/* Project Types */}
        <section className="py-16 md:py-24 bg-background-alt" aria-labelledby="project-types-heading">
          <div className="container-custom">
            <h2 id="project-types-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Our Project Experience</h2>
            <p className="text-xl text-secondary mb-12 text-center">Types of projects we excel at</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Corporate Websites</h3>
                <p className="text-secondary mb-4">
                  Professional websites for businesses that want to establish a strong online presence. Includes company information, services, testimonials, and contact details.
                </p>
                <p className="text-sm text-gray-500">Starting from £2,500</p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">E-commerce Solutions</h3>
                <p className="text-secondary mb-4">
                  Full-featured online stores with product management, secure payment processing, inventory tracking, and customer accounts.
                </p>
                <p className="text-sm text-gray-500">Starting from £5,000</p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Web Applications</h3>
                <p className="text-secondary mb-4">
                  Custom web applications designed for specific business needs, such as booking systems, customer portals, or management tools.
                </p>
                <p className="text-sm text-gray-500">Starting from £7,500</p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Content Management Systems</h3>
                <p className="text-secondary mb-4">
                  Dynamic websites with easy-to-use content management, allowing you to update content without technical knowledge.
                </p>
                <p className="text-sm text-gray-500">Starting from £4,000</p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Website Redesigns</h3>
                <p className="text-secondary mb-4">
                  Transform your existing website with a modern design, improved user experience, and better performance.
                </p>
                <p className="text-sm text-gray-500">Starting from £3,500</p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Monthly Maintenance</h3>
                <p className="text-secondary mb-4">
                  Ongoing website care including security updates, content changes, performance optimization, and technical support.
                </p>
                <p className="text-sm text-gray-500">Starting from £250/month</p>
              </motion.div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-secondary mb-6 max-w-2xl mx-auto">
                Please note that these are starting prices and may vary based on project specifics. We'll provide a detailed, customized quote based on your requirements.
              </p>
            </div>
          </div>
        </section>
      </main>
    </PageTransition>
  );
};

export default QuotePage;